import type { MaybeRefOrGetter, ComputedRef, Ref } from 'vue'
import { computed, onMounted, ref, toValue, watch } from 'vue'

import type { LocationQueryRaw } from 'vue-router'

import { TRACKING_EVENTS } from '~/constants/trackingEvents'

import {
  findDomainOption,
  findFormationTypeOption,
  findLevelOption,
  findSchoolTypeOption,
} from '~/composables/establishmentSearchFilterLists'
import type {
  EstablishmentActiveFilterChip,
  EstablishmentFilterQueryParam,
  EstablishmentSearchBBox,
  EstablishmentSearchDisplayKind,
  EstablishmentSearchFilterInput,
  EstablishmentSearchProgramsResponse,
  EstablishmentSearchSchoolsResponse,
} from '~/types/EstablishmentSearch'
import type { BlogSearchSeoPayload } from '~/types/BlogSearchHub'
import type {
  EstablishmentProgramRow,
  EstablishmentSchoolRow,
} from '~/types/EstablishmentSearchRows'
import type {
  EstablishmentSearchGeoTrigger,
  EstablishmentSearchTrackingContext,
  EstablishmentSearchTrigger,
} from '~/types/Tracking'
import {
  buildEstablishmentSearchGeoPayload,
  resolveEstablishmentSearchGeoTrigger,
  toEstablishmentSearchExecutedPayload,
  toEstablishmentSearchResultPreviewItems,
  toEstablishmentSearchTrackingContext,
} from '~/composables/useTracking'
import {
  buildActiveFilterChips,
  buildEstablishmentFilterInput,
  buildEstablishmentSearchApiBody,
  buildEstablishmentSearchCacheKey,
  buildEstablishmentSearchFiltersLabelsPayload,
  buildQueryClearingFilters,
  buildQueryWithDisplayKind,
  buildQueryWithFilterParam,
  buildQueryWithMapBounds,
  buildQueryWithMapSearch,
  buildQueryWithMobileView,
  buildQueryWithSearchText,
  buildSlugsAfterChipRemoval,
  FRANCE_DEFAULT_SEARCH_BBOX,
  parseCommaSlugList,
  parseEstablishmentBBox,
  parseEstablishmentView,
  parseMapSearchFlag,
  parseRouteQueryString,
  getStoredSearchBBox,
  setStoredSearchBBox,
} from '~/utils/establishmentSearchUrl'
import { getUserSearchBBox } from '~/utils/geocoding'

interface UseEstablishmentSearchHubOptions {
  readonly displayKind: MaybeRefOrGetter<EstablishmentSearchDisplayKind>
}

interface EstablishmentSearchData {
  readonly schoolRows: EstablishmentSchoolRow[]
  readonly programRows: EstablishmentProgramRow[]
}

/**
 * Hub unique pour `/recherche/etablissement` : URL, filtres, SEO et résultats.
 * @param {UseEstablishmentSearchHubOptions} options - `displayKind` réactif (`school` / `program`).
 * @returns {object} État route, actions filtres, SEO et lignes écoles/formations.
 */
export function useEstablishmentSearchHub(options: UseEstablishmentSearchHubOptions) {
  const api = useFrontApi()
  const { locale } = useI18n()
  const route = useRoute()
  const router = useRouter()

  const searchDraft: Ref<string> = ref<string>('')
  const appliedSearch: Ref<string> = ref<string>('')
  const activeBbox: Ref<EstablishmentSearchBBox> = ref<EstablishmentSearchBBox>({
    ...FRANCE_DEFAULT_SEARCH_BBOX,
  })
  const initialMapBounds: Ref<EstablishmentSearchBBox | null> = ref<EstablishmentSearchBBox | null>(
    null,
  )
  const searchFetchGeneration: Ref<number> = ref<number>(0)
  const pendingSearchTrigger: Ref<EstablishmentSearchTrigger> =
    ref<EstablishmentSearchTrigger>('initial_load')
  const pendingGeoTrigger: Ref<EstablishmentSearchGeoTrigger> = ref<EstablishmentSearchGeoTrigger>(
    parseEstablishmentBBox(route.query.bbox) !== null && parseMapSearchFlag(route.query.mapSearch)
      ? 'url_restored'
      : 'default_france',
  )
  const lastEstablishmentSearchTrackingContext: Ref<EstablishmentSearchTrackingContext | null> =
    ref<EstablishmentSearchTrackingContext | null>(null)

  const { track } = useTracking()

  const mobileView: ComputedRef<'map' | 'list'> = computed((): 'map' | 'list' =>
    parseEstablishmentView(route.query.view),
  )
  const typeSlugs: ComputedRef<string[]> = computed((): string[] =>
    parseCommaSlugList(route.query.type),
  )
  const domainSlugs: ComputedRef<string[]> = computed((): string[] =>
    parseCommaSlugList(route.query.domain),
  )
  const levelSlugs: ComputedRef<string[]> = computed((): string[] =>
    parseCommaSlugList(route.query.level),
  )
  const formationTypeSlugs: ComputedRef<string[]> = computed((): string[] =>
    parseCommaSlugList(route.query.ftype),
  )
  const mapSearchEnabled: ComputedRef<boolean> = computed((): boolean =>
    parseMapSearchFlag(route.query.mapSearch),
  )

  const filterInput: ComputedRef<EstablishmentSearchFilterInput> = computed(
    (): EstablishmentSearchFilterInput =>
      buildEstablishmentFilterInput({
        q: appliedSearch.value,
        typeSlugs: typeSlugs.value,
        domainSlugs: domainSlugs.value,
        levelSlugs: levelSlugs.value,
        formationTypeSlugs: formationTypeSlugs.value,
      }),
  )

  const activeChips: ComputedRef<EstablishmentActiveFilterChip[]> = computed(
    (): EstablishmentActiveFilterChip[] =>
      buildActiveFilterChips([
        { param: 'type', slugs: typeSlugs.value, findOption: findSchoolTypeOption },
        { param: 'domain', slugs: domainSlugs.value, findOption: findDomainOption },
        { param: 'level', slugs: levelSlugs.value, findOption: findLevelOption },
        { param: 'ftype', slugs: formationTypeSlugs.value, findOption: findFormationTypeOption },
      ]),
  )

  function replaceQuery(nextQuery: LocationQueryRaw): void {
    void router.replace({ path: route.path, query: nextQuery })
  }

  function bumpSearchFetch(): void {
    searchFetchGeneration.value++
  }

  function setMobileView(view: 'map' | 'list'): void {
    void router.replace({
      path: route.path,
      query: buildQueryWithMobileView(route.query, view),
    })
  }

  function onKindChange(value: string): void {
    if (value !== 'school' && value !== 'program') return
    pendingSearchTrigger.value = 'kind_switch'
    void router.push({
      path: route.path,
      query: buildQueryWithDisplayKind(route.query, value),
    })
  }

  function updateFilterQueryParam(param: EstablishmentFilterQueryParam, slugs: string[]): void {
    pendingSearchTrigger.value = 'filter'
    replaceQuery(buildQueryWithFilterParam(route.query, param, slugs))
  }

  function removeFilterChip(param: EstablishmentFilterQueryParam, slugToRemove: string): void {
    const currentSlugsByParam: Record<EstablishmentFilterQueryParam, string[]> = {
      type: typeSlugs.value,
      domain: domainSlugs.value,
      level: levelSlugs.value,
      ftype: formationTypeSlugs.value,
    }
    const remainingSlugs: string[] = buildSlugsAfterChipRemoval(
      currentSlugsByParam[param],
      slugToRemove,
    )
    updateFilterQueryParam(param, remainingSlugs)
  }

  function clearAllFilters(): void {
    pendingSearchTrigger.value = 'clear_all'
    pendingGeoTrigger.value = mapSearchEnabled.value ? 'map_apply' : 'default_france'
    replaceQuery(buildQueryClearingFilters(route.query))
    appliedSearch.value = ''
    searchDraft.value = ''
  }

  function onUpdateMapSearch(enabled: boolean): void {
    const displayKind: EstablishmentSearchDisplayKind = toValue(options.displayKind)
    track(TRACKING_EVENTS.ESTABLISHMENT_SEARCH_MAP_MODE_TOGGLED, {
      enabled,
      display_kind: displayKind,
    })
    replaceQuery(buildQueryWithMapSearch(route.query, enabled))
  }

  function onApplyBounds(bbox: EstablishmentSearchBBox): void {
    pendingSearchTrigger.value = 'map_apply'
    pendingGeoTrigger.value = 'map_apply'
    activeBbox.value = bbox
    setStoredSearchBBox(bbox)
    replaceQuery(buildQueryWithMapBounds(route.query, bbox))
    bumpSearchFetch()
  }

  /**
   * Met à jour la bbox active ; refetch seulement si `shouldFetch` (case carte cochée / appliquer zone).
   * @param {EstablishmentSearchBBox} bbox - Zone courante de la carte.
   * @param {boolean} shouldFetch - Déclencher un appel API.
   * @returns {void}
   */
  function onMapBoundsChange(bbox: EstablishmentSearchBBox, shouldFetch: boolean): void {
    activeBbox.value = bbox
    if (!shouldFetch) return
    setStoredSearchBBox(bbox)
    pendingSearchTrigger.value = 'map_bounds'
    pendingGeoTrigger.value = 'map_pan'
    bumpSearchFetch()
  }

  function onSubmitSearch(value: string): void {
    pendingSearchTrigger.value = 'text_search'
    appliedSearch.value = value
    replaceQuery(buildQueryWithSearchText(route.query, value))
  }

  /**
   * Émet `establishment_search_executed` après chaque réponse API côté client.
   * Appelé par le `watch(searchData)` dans `onMounted` — jamais dans le fetcher (évite double comptage SSR).
   * @param {EstablishmentSearchData} data - Dernière réponse recherche.
   * @returns {Promise<void>}
   */
  async function trackEstablishmentSearchExecuted(data: EstablishmentSearchData): Promise<void> {
    const displayKind: EstablishmentSearchDisplayKind = toValue(options.displayKind)
    const input: EstablishmentSearchFilterInput = filterInput.value
    const query: string = input.q.trim()
    const filters = buildEstablishmentSearchFiltersLabelsPayload(displayKind, input)
    const filterCount: number =
      filters.type.length + filters.domain.length + filters.level.length + filters.ftype.length
    const mapSearchOn: boolean = mapSearchEnabled.value
    const triggerGeo: EstablishmentSearchGeoTrigger = resolveEstablishmentSearchGeoTrigger({
      pendingGeoTrigger: pendingGeoTrigger.value,
      mapSearchOn,
      bbox: activeBbox.value,
    })
    const geo = await buildEstablishmentSearchGeoPayload(activeBbox.value, mapSearchOn, triggerGeo)
    const resultsCount: number =
      displayKind === 'school' ? data.schoolRows.length : data.programRows.length
    const items = toEstablishmentSearchResultPreviewItems(
      displayKind,
      data.schoolRows,
      data.programRows,
    )

    track(
      TRACKING_EVENTS.ESTABLISHMENT_SEARCH_EXECUTED,
      toEstablishmentSearchExecutedPayload({
        display_kind: displayKind,
        query,
        filters,
        geo,
        resultsCount,
        items,
        trigger: pendingSearchTrigger.value,
        filterCount,
        hasTextQuery: query.length > 0,
      }),
    )

    lastEstablishmentSearchTrackingContext.value = toEstablishmentSearchTrackingContext({
      display_kind: displayKind,
      query,
      filters,
      results_count: resultsCount,
      map_search_enabled: mapSearchOn,
      geo,
    })
  }

  watch(
    (): LocationQueryRaw['q'] => route.query.q,
    (): void => {
      const searchText: string = parseRouteQueryString(route.query.q)
      searchDraft.value = searchText
      appliedSearch.value = searchText
    },
    { immediate: true },
  )

  const { data: seoPayload } = useAsyncData<BlogSearchSeoPayload | null>(
    () => `establishment-search-seo-${locale.value}-${toValue(options.displayKind)}`,
    async () => {
      const kind: EstablishmentSearchDisplayKind = toValue(options.displayKind)
      const pageType: string = kind === 'program' ? 'degrees' : 'schools'
      const { data } = await useFrontApi().get<BlogSearchSeoPayload>('/api/aca/seointer', {
        params: { language: locale.value, pageType },
      })
      return data
    },
    {
      watch: [
        (): string => locale.value,
        (): EstablishmentSearchDisplayKind => toValue(options.displayKind),
      ],
    },
  )

  const {
    data: searchData,
    status: searchStatus,
    execute: executeSearch,
  } = useAsyncData<EstablishmentSearchData>(
    () => {
      const kind: EstablishmentSearchDisplayKind = toValue(options.displayKind)
      const input: EstablishmentSearchFilterInput = filterInput.value
      return `establishment-search-${locale.value}-${buildEstablishmentSearchCacheKey(
        kind,
        input,
        searchFetchGeneration.value,
      )}`
    },
    async (): Promise<EstablishmentSearchData> => {
      const kind: EstablishmentSearchDisplayKind = toValue(options.displayKind)
      const input: EstablishmentSearchFilterInput = filterInput.value
      const body = buildEstablishmentSearchApiBody(locale.value, kind, input, activeBbox.value)

      if (kind === 'school') {
        const { data } = await api.post<EstablishmentSearchSchoolsResponse>(
          '/api/search/schools',
          body,
        )
        return { schoolRows: data ?? [], programRows: [] }
      }

      const { data } = await api.post<EstablishmentSearchProgramsResponse>(
        '/api/search/programs',
        body,
      )
      return { schoolRows: [], programRows: data ?? [] }
    },
    {
      server: false,
      lazy: true,
      immediate: false,
      watch: [
        (): string => locale.value,
        (): EstablishmentSearchDisplayKind => toValue(options.displayKind),
        filterInput,
        searchFetchGeneration,
      ],
      deep: true,
    },
  )

  const schoolRows = computed<EstablishmentSchoolRow[]>(
    (): EstablishmentSchoolRow[] => searchData.value?.schoolRows ?? [],
  )
  const programRows = computed<EstablishmentProgramRow[]>(
    (): EstablishmentProgramRow[] => searchData.value?.programRows ?? [],
  )

  // `searchData` garde l'ancienne valeur pendant un refetch : on s'appuie sur `status` ('pending'),
  // avec le null en filet de sécurité pour l'état initial.
  const isSearchPending: ComputedRef<boolean> = computed(
    (): boolean => searchStatus.value === 'pending' || searchData.value == null,
  )

  /**
   * Zone de recherche initiale (client) : URL → dernière zone (localStorage) → géoloc approximative
   * de l'utilisateur → France. Déclenche le premier fetch dans tous les cas.
   * @returns {Promise<void>}
   */
  async function resolveInitialSearchBBox(): Promise<void> {
    try {
      const urlBbox: EstablishmentSearchBBox | null = parseEstablishmentBBox(route.query.bbox)
      if (urlBbox) {
        activeBbox.value = urlBbox
        initialMapBounds.value = urlBbox
        return
      }
      const storedBbox: EstablishmentSearchBBox | null = getStoredSearchBBox()
      if (storedBbox) {
        activeBbox.value = storedBbox
        initialMapBounds.value = storedBbox
        return
      }
      const geoBbox: EstablishmentSearchBBox | undefined = await getUserSearchBBox()
      if (geoBbox) {
        activeBbox.value = geoBbox
        initialMapBounds.value = geoBbox
      }
    } finally {
      void executeSearch()
    }
  }

  onMounted((): void => {
    void resolveInitialSearchBBox()
    watch(
      (): EstablishmentSearchData | null | undefined => searchData.value,
      (data: EstablishmentSearchData | null | undefined): void => {
        if (data == null) return
        void trackEstablishmentSearchExecuted(data)
      },
      { immediate: true },
    )
  })

  return {
    searchDraft,
    mobileView,
    typeSlugs,
    domainSlugs,
    levelSlugs,
    formationTypeSlugs,
    mapSearchEnabled,
    activeChips,
    setMobileView,
    onKindChange,
    updateFilterQueryParam,
    removeFilterChip,
    clearAllFilters,
    onUpdateMapSearch,
    onApplyBounds,
    onMapBoundsChange,
    onSubmitSearch,
    seoPayload,
    schoolRows,
    programRows,
    isSearchPending,
    initialMapBounds,
    lastEstablishmentSearchTrackingContext,
  }
}
